import React, { useState } from "react";
import type { WidgetInfo } from "./widget-declarations";
import { motion, AnimatePresence } from "framer-motion";

interface WidgetSettingsProps {
  widget: WidgetInfo;
  onClose: () => void;
  onSave: (updatedWidget: WidgetInfo) => void;
}

const WidgetSettings: React.FC<WidgetSettingsProps> = ({
  widget,
  onClose,
  onSave,
}) => {
  const [editedWidget, setEditedWidget] = useState<WidgetInfo>({ ...widget });

  // مجموعة من الألوان الجميلة للاختيار من بينها على طراز أبل
  const colorPalette = [
    "rgba(23, 92, 230, 0.15)", // أزرق
    "rgba(70, 132, 242, 0.15)", // أزرق فاتح
    "rgba(242, 70, 180, 0.15)", // وردي
    "rgba(242, 159, 70, 0.15)", // برتقالي
    "rgba(70, 214, 242, 0.15)", // سماوي
    "rgba(242, 225, 70, 0.15)", // أصفر
    "rgba(29, 185, 84, 0.15)", // أخضر
    "rgba(255, 123, 0, 0.15)", // برتقالي غامق
    "rgba(156, 39, 176, 0.15)", // بنفسجي
    "rgba(244, 67, 54, 0.15)", // أحمر
  ];

  // تحديث حجم الويدجت
  const handleSizeChange = (newSize: "small" | "medium" | "large") => {
    setEditedWidget({ ...editedWidget, size: newSize });
  };

  // تحديث لون الويدجت
  const handleColorChange = (newColor: string) => {
    setEditedWidget({ ...editedWidget, bgColor: newColor });
  };

  // معالجة الحفظ
  const handleSave = () => {
    onSave(editedWidget);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-black/80 backdrop-blur-xl rounded-3xl p-6 max-w-md w-full mx-4 shadow-2xl border border-white/10"
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">إعدادات الويدجت</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="mb-6">
            <div className="flex items-center mb-4">
              <div
                className="w-10 h-10 flex items-center justify-center rounded-full mr-3"
                style={{
                  backgroundColor:
                    editedWidget.bgColor || "rgba(255, 255, 255, 0.1)",
                }}
              >
                <img
                  src={editedWidget.icon}
                  alt={editedWidget.title}
                  className="w-6 h-6"
                />
              </div>
              <span className="text-white font-semibold text-lg">
                {editedWidget.title}
              </span>
            </div>

            {/* تحديد حجم الويدجت */}
            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium mb-3">
                حجم الويدجت
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => handleSizeChange("small")}
                  className={`
                    py-2 px-4 rounded-full text-sm font-medium text-center
                    transition-all duration-200
                    ${
                      editedWidget.size === "small"
                        ? "bg-white/20 text-white ring-2 ring-white/70"
                        : "bg-black/40 text-gray-300 hover:bg-black/50"
                    }
                  `}
                >
                  صغير
                </button>
                <button
                  onClick={() => handleSizeChange("medium")}
                  className={`
                    py-2 px-4 rounded-full text-sm font-medium text-center
                    transition-all duration-200
                    ${
                      editedWidget.size === "medium"
                        ? "bg-white/20 text-white ring-2 ring-white/70"
                        : "bg-black/40 text-gray-300 hover:bg-black/50"
                    }
                  `}
                >
                  متوسط
                </button>
                <button
                  onClick={() => handleSizeChange("large")}
                  className={`
                    py-2 px-4 rounded-full text-sm font-medium text-center
                    transition-all duration-200
                    ${
                      editedWidget.size === "large"
                        ? "bg-white/20 text-white ring-2 ring-white/70"
                        : "bg-black/40 text-gray-300 hover:bg-black/50"
                    }
                  `}
                >
                  كبير
                </button>
              </div>
            </div>

            {/* اختيار لون الويدجت */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-3">
                لون الويدجت
              </label>
              <div className="grid grid-cols-5 gap-3">
                {colorPalette.map((color, index) => (
                  <button
                    key={index}
                    onClick={() => handleColorChange(color)}
                    className={`
                      w-full h-10 rounded-full transition-all duration-200
                      ${
                        editedWidget.bgColor === color
                          ? "ring-2 ring-white/70"
                          : "ring-1 ring-white/10"
                      }
                    `}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* أزرار الإجراءات */}
          <div className="flex justify-end space-x-3 rtl:space-x-reverse mt-8">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-full bg-black/40 text-gray-300 hover:bg-black/50 transition-colors"
            >
              إلغاء
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
            >
              حفظ التغييرات
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default WidgetSettings;
